// #/claim — a provider takes ownership of the listing we already hold for them.
//
// Three ways in, all landing on #/edit:
//   1. QR / link with ?code=XXXXXX  → resolve the code, confirm the business, claim
//   2. typed code                    → same, entered by hand
//   3. search by name                → for the 7 profiles that carry an email, the
//                                      claim succeeds on a confirmed email match alone
//
// Sign-in comes FIRST, always: claim_provider_profile reads auth.uid() inside the
// database, so there is no claim without a session. Note shouldCreateUser: true —
// unlike #/signin, the person arriving here has almost certainly never had an
// account (142 of 152 profiles were seeded with a 'placeholder-' id and no login),
// so refusing to create one would make the QR a dead end.

import { supabase } from '../lib/supabase';
import { getOptionalSession, signOut } from '../lib/auth-guard';
import { publicRedirect } from '../lib/canonical';
import { hashQuery } from '../lib/router';
import { rememberReturnTo } from '../lib/return-to';
import { getTurnstileToken, captchaOptions, turnstileEnabled } from '../lib/turnstile';
import {
  searchClaimable,
  resolveClaimCode,
  claimProfile,
  normaliseCode,
  type ClaimableProfile,
} from '../lib/claim-api';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};
function esc(s: string | null | undefined): string {
  return (s ?? '').replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] ?? c);
}

export async function renderClaim(container: HTMLElement): Promise<void> {
  const session = await getOptionalSession();
  const codeFromUrl = normaliseCode(hashQuery().get('code') ?? '');

  if (!session) {
    renderSignInFirst(container, codeFromUrl);
    return;
  }
  renderClaimFlow(container, codeFromUrl, session.user.email ?? null);
}

// ── Step 0: sign in ────────────────────────────────────────────────
function renderSignInFirst(container: HTMLElement, code: string): void {
  container.innerHTML = `
    <main class="cl-shell">
      <div class="cl-card">
        <div class="cl-logo">Curate</div>
        <h1>Claim your business</h1>
        <p class="cl-lead">
          ${code
            ? 'Enter your email and we&rsquo;ll send you a one-tap sign-in link. Your code is already saved.'
            : 'First, your email. We&rsquo;ll send you a one-tap sign-in link — no password to remember.'}
        </p>
        <form id="cl-signin">
          <label for="cl-email">Email</label>
          <input type="email" id="cl-email" name="email" required autocomplete="email"
                 inputmode="email" placeholder="you@yourbusiness.com" />
<div class="ts-mount" id="ts-claim"></div>
          <button type="submit" class="cl-btn">Send me a sign-in link</button>
        </form>
        <p id="cl-status" class="status" aria-live="polite"></p>
        <p class="cl-foot">Curate is operated by E-Bikes Florence. Stuck? Email john@ebikesflorence.com.</p>
      </div>
    </main>
  `;

  const form = container.querySelector<HTMLFormElement>('#cl-signin');
  const status = container.querySelector<HTMLElement>('#cl-status');
  if (!form || !status) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = String(new FormData(form).get('email') ?? '').trim().toLowerCase();
    if (!email) return;

    status.textContent = 'Sending…';
    status.className = 'status';

    // Come back to THIS route (code included) after the magic link, instead of
    // main.ts's default bounce to #/edit.
    rememberReturnTo(code ? `#/claim?code=${encodeURIComponent(code)}` : '#/claim');

    // Turnstile is inert unless VITE_TURNSTILE_SITE_KEY is set; a failed or
    // unavailable challenge yields null and the request proceeds. The
    // server-side caps are the control that always holds (WP5).
    const tsMount = container.querySelector<HTMLElement>('#ts-claim');
    const tsToken = tsMount ? await getTurnstileToken(tsMount) : null;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: publicRedirect(), shouldCreateUser: true, ...captchaOptions(tsToken) },
    });

    if (error) {
      status.textContent = `Sorry — ${error.message}. Try again, or email john@ebikesflorence.com.`;
      status.className = 'status status-error';
      return;
    }
    status.textContent = `Check your inbox for ${email}. The link expires in 60 minutes.`;
    status.className = 'status status-success';
    form.querySelector<HTMLButtonElement>('button[type="submit"]')!.disabled = true;
  });
}

// ── Steps 1–3: resolve / search / claim ────────────────────────────
function renderClaimFlow(container: HTMLElement, codeFromUrl: string, email: string | null): void {
  container.innerHTML = `
    <main class="cl-shell">
      <div class="cl-card">
        <div class="cl-logo">Curate</div>
        <h1>Claim your business</h1>
        <p class="cl-lead">Signed in as <strong>${esc(email ?? 'your account')}</strong>.
          <button type="button" class="cl-link" id="cl-signout">Not you?</button></p>

        <section id="cl-confirm" hidden></section>

        <section id="cl-find">
          <label for="cl-code">Have a code from John?</label>
          <div class="cl-row">
            <input type="text" id="cl-code" maxlength="8" autocapitalize="characters"
                   autocomplete="off" spellcheck="false" placeholder="e.g. K7M4QP" />
            <button type="button" class="cl-btn cl-btn-sm" id="cl-code-go">Use code</button>
          </div>

          <div class="cl-or"><span>or find your business</span></div>

          <label for="cl-search">Business name</label>
          <input type="search" id="cl-search" autocomplete="off" placeholder="Start typing…" />
          <ul class="cl-results" id="cl-results"></ul>
        </section>

        <p id="cl-status" class="status" aria-live="polite"></p>
        <p class="cl-foot">Can&rsquo;t find it? Email john@ebikesflorence.com and we&rsquo;ll sort it out.</p>
      </div>
    </main>
  `;

  const q = <T extends HTMLElement>(sel: string): T => container.querySelector<T>(sel)!;
  const status = q<HTMLElement>('#cl-status');
  const confirmPane = q<HTMLElement>('#cl-confirm');
  const findPane = q<HTMLElement>('#cl-find');
  const results = q<HTMLUListElement>('#cl-results');

  const setStatus = (msg: string, kind: '' | 'success' | 'error'): void => {
    status.textContent = msg;
    status.className = kind ? `status status-${kind}` : 'status';
  };

  q<HTMLButtonElement>('#cl-signout').addEventListener('click', () => void signOut());

  // Show the "Is this your business?" confirmation for one profile.
  const showConfirm = (profile: { id: string; business_name: string; address?: string | null },
                       code: string): void => {
    findPane.hidden = true;
    confirmPane.hidden = false;
    confirmPane.innerHTML = `
      <div class="cl-confirm-card">
        <p class="cl-eyebrow">Is this your business?</p>
        <h2>${esc(profile.business_name)}</h2>
        ${profile.address ? `<p class="cl-addr">${esc(profile.address)}</p>` : ''}
        <button type="button" class="cl-btn" id="cl-do-claim">Yes — this is mine</button>
        <button type="button" class="cl-link" id="cl-back">No, let me look again</button>
      </div>
    `;
    confirmPane.querySelector<HTMLButtonElement>('#cl-back')!.addEventListener('click', () => {
      confirmPane.hidden = true;
      confirmPane.innerHTML = '';
      findPane.hidden = false;
      setStatus('', '');
    });
    confirmPane.querySelector<HTMLButtonElement>('#cl-do-claim')!.addEventListener('click', () => {
      void doClaim(profile.id, code);
    });
  };

  const doClaim = async (profileId: string, code: string): Promise<void> => {
    setStatus('Claiming…', '');
    try {
      const result = await claimProfile(profileId, code);
      if (result.ok) {
        setStatus('Done — opening your profile…', 'success');
        window.location.hash = '#/edit';
        return;
      }
      // The database writes the provider-facing wording; show it verbatim.
      setStatus(result.message ?? 'We could not complete that claim.', 'error');
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Something went wrong. Try again.', 'error');
    }
  };

  // ── code path ──
  const tryCode = async (raw: string): Promise<void> => {
    const code = normaliseCode(raw);
    if (code.length === 0) return;
    setStatus('Checking that code…', '');
    try {
      const resolved = await resolveClaimCode(code);
      if (!resolved.ok || !resolved.profile_id || !resolved.business_name) {
        setStatus(codeMessage(resolved.reason), 'error');
        return;
      }
      setStatus('', '');
      showConfirm(
        { id: resolved.profile_id, business_name: resolved.business_name, address: resolved.address },
        code,
      );
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Could not check that code.', 'error');
    }
  };

  q<HTMLButtonElement>('#cl-code-go').addEventListener('click', () => {
    void tryCode(q<HTMLInputElement>('#cl-code').value);
  });
  q<HTMLInputElement>('#cl-code').addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') {
      e.preventDefault();
      void tryCode(q<HTMLInputElement>('#cl-code').value);
    }
  });

  // ── search path ──
  let searchSeq = 0;
  const runSearch = async (term: string): Promise<void> => {
    const seq = ++searchSeq;
    if (term.trim().length < 2) {
      results.innerHTML = '';
      return;
    }
    try {
      const rows = await searchClaimable(term);
      if (seq !== searchSeq) return;  // a later keystroke already won
      results.innerHTML = rows.length === 0
        ? '<li class="cl-empty">Nothing with that name yet.</li>'
        : rows.map(renderResult).join('');
      results.querySelectorAll<HTMLButtonElement>('button[data-id]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const row = rows.find((r) => r.id === btn.dataset.id);
          if (!row) return;
          // No code in hand: the claim can still succeed on a confirmed email
          // match, and the database says so plainly if it cannot.
          showConfirm(row, q<HTMLInputElement>('#cl-code').value);
        });
      });
    } catch (err) {
      if (seq !== searchSeq) return;
      setStatus(err instanceof Error ? err.message : 'Search failed.', 'error');
    }
  };

  let debounce: number | undefined;
  q<HTMLInputElement>('#cl-search').addEventListener('input', (e) => {
    const term = (e.target as HTMLInputElement).value;
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => void runSearch(term), 220);
  });

  // Arrived from a QR: resolve immediately.
  if (codeFromUrl) {
    const input = q<HTMLInputElement>('#cl-code');
    input.value = codeFromUrl;
    void tryCode(codeFromUrl);
  }
}

function renderResult(row: ClaimableProfile): string {
  const sub = [row.business_type, row.address].filter(Boolean).join(' · ');
  if (row.already_claimed) {
    return `<li class="cl-result cl-taken">
      <div><strong>${esc(row.business_name)}</strong>${sub ? `<span>${esc(sub)}</span>` : ''}</div>
      <span class="cl-badge">Already claimed</span>
    </li>`;
  }
  return `<li class="cl-result">
    <div><strong>${esc(row.business_name)}</strong>${sub ? `<span>${esc(sub)}</span>` : ''}</div>
    <button type="button" data-id="${esc(row.id)}" class="cl-btn cl-btn-sm">This is mine</button>
  </li>`;
}

function codeMessage(reason: string | undefined): string {
  switch (reason) {
    case 'code_used':      return 'That code has already been used. Ask John for a fresh one.';
    case 'code_expired':   return 'That code has expired. Ask John for a fresh one.';
    case 'already_claimed':return 'That business has already been claimed. Email john@ebikesflorence.com if that is wrong.';
    case 'not_signed_in':  return 'Please sign in first.';
    default:               return 'We don’t recognise that code. Check the letters and try again.';
  }
}
